import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Search, CreditCard, CheckCircle, XCircle, Clock, Download } from "lucide-react"
import axios from "@/utils/axios"
import Loader from "@/components/loader/loader"
import { formatCurrency } from "@/utils/format"
import { toast } from "sonner"

export default function Transactions() {
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    gateway: "",
  })

  const fetchTransactions = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      }

      if (filters.search) params.search = filters.search
      if (filters.status) params.status = filters.status
      if (filters.gateway) params.gateway = filters.gateway

      const res = await axios.get("/admin/transactions", { params })
      
      if (res.data.success) {
        setTransactions(res.data.data || [])
        setPagination(prev => ({
          ...prev,
          total: res.data.pagination?.total || 0,
          totalPages: res.data.pagination?.totalPages || 0,
        }))
      }
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to fetch transactions"
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, filters.status, filters.gateway])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }))
    }, 500)

    return () => clearTimeout(timer)
  }, [filters.search])

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: pageNumber }))
    }
  }

  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }))
  }

  const handleStatusFilterChange = (value) => {
    setFilters(prev => ({ ...prev, status: value === "all" ? "" : value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleGatewayFilterChange = (value) => {
    setFilters(prev => ({ ...prev, gateway: value === "all" ? "" : value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || ""
    
    if (statusLower === "success" || statusLower === "successful") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Success
        </span>
      )
    }
    
    if (statusLower === "pending") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      )
    }
    
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <XCircle className="w-3 h-3" />
        {status || "Failed"}
      </span>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getUserName = (transaction) => {
    if (transaction.first_name || transaction.last_name) {
      return `${transaction.first_name || ""} ${transaction.last_name || ""}`.trim()
    }
    return transaction.user_email || transaction.email || "N/A"
  }

  if (loading && transactions.length === 0) {
    return <Loader />
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <CreditCard className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-primary">Transactions Management</h1>
            <p className="text-gray-600 mt-1">View and manage all user transactions across the platform.</p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search by reference, email, or user name..."
            className="pl-9 pr-4 py-2 rounded-md border border-input focus:ring-primary focus:border-primary"
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <Select onValueChange={handleStatusFilterChange} value={filters.status || "all"}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="successful">Successful</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select onValueChange={handleGatewayFilterChange} value={filters.gateway || "all"}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by gateway" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Gateways</SelectItem>
            <SelectItem value="paystack">Paystack</SelectItem>
            <SelectItem value="flutterwave">Flutterwave</SelectItem>
            <SelectItem value="stripe">Stripe</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transactions List Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-primary">All Transactions</CardTitle>
              <CardDescription>
                Total: {pagination.total} transactions
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <CreditCard className="w-16 h-16 mb-4" />
              <p className="text-lg">{error}</p>
              <Button onClick={fetchTransactions} className="mt-4">
                Retry
              </Button>
            </div>
          ) : transactions.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reference</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Gateway</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id || transaction.reference}>
                        <TableCell className="font-mono text-sm">
                          {transaction.reference || "N/A"}
                        </TableCell>
                        <TableCell>
                          {getUserName(transaction)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {transaction.email || transaction.user_email || "N/A"}
                        </TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(transaction.amount || 0)}
                        </TableCell>
                        <TableCell>
                          <span className="capitalize">{transaction.gateway || "N/A"}</span>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(transaction.status)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(transaction.date)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Pagination className="mt-8">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          handlePageChange(pagination.page - 1)
                        }}
                        className={pagination.page <= 1 ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                    {[...Array(pagination.totalPages)].map((_, i) => {
                      const pageNum = i + 1
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === pagination.totalPages ||
                        (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                      ) {
                        return (
                          <PaginationItem key={i}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault()
                                handlePageChange(pageNum)
                              }}
                              isActive={pagination.page === pageNum}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      } else if (
                        pageNum === pagination.page - 2 ||
                        pageNum === pagination.page + 2
                      ) {
                        return (
                          <PaginationItem key={i}>
                            <span className="px-2">...</span>
                          </PaginationItem>
                        )
                      }
                      return null
                    })}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          handlePageChange(pagination.page + 1)
                        }}
                        className={pagination.page >= pagination.totalPages ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <CreditCard className="w-16 h-16 mx-auto mb-4" />
              <p className="text-lg">No transactions found.</p>
              {filters.search || filters.status || filters.gateway ? (
                <p className="text-sm mt-2">Try adjusting your filters.</p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

